/**
 * PGD 2a: Aciclovir for shingles (herpes zoster)
 * Version: 1.0
 * Effective: 2024-01-31
 * Expiry: 2027-01-30
 *
 * QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
 */

import type { PGD } from "../types";

export const pgdAciclovirShingles: PGD = {
  id: "pgd-2a-aciclovir-shingles",
  reference: "2a",
  version: "1.0",
  drugRef: "aciclovir",
  indication: "Treatment of acute herpes zoster (shingles) in eligible adults",
  validFrom: "2024-01-31",
  expiryDate: "2027-01-30",
  frameworkType: "pharmacy_first",

  population: {
    ageRange: { min: 18, max: 999 },
    pregnancyAllowed: false,
    breastfeedingAllowed: true,
    notes:
      "Breastfeeding allowed with caution — aciclovir is excreted in breast milk; consider clinical judgement.",
  },

  duration: { days: 7 },

  doseBands: [
    {
      id: "aciclovir-adult",
      resolveBy: "single",
      dose: 800,
      frequency: "FIVE times a day",
      formulations: [
        {
          formulationId: "aciclovir-800-tab",
          instruction:
            "Take ONE 800mg tablet FIVE times a day for 7 days (approx. every 4 hours during waking hours)",
          quantity: { count: 35, label: "35 tablets" },
        },
        {
          formulationId: "aciclovir-400-tab",
          instruction:
            "Take TWO 400mg tablets FIVE times a day for 7 days (approx. every 4 hours during waking hours)",
          quantity: { count: 70, label: "70 tablets" },
        },
        {
          formulationId: "aciclovir-200-tab",
          instruction:
            "Take FOUR 200mg tablets FIVE times a day for 7 days (approx. every 4 hours during waking hours)",
          quantity: { count: 140, label: "140 tablets" },
        },
      ],
    },
  ],

  preSupplyChecks: [
    {
      id: "ex-allergy",
      question:
        "Is the patient allergic to aciclovir, valaciclovir or any excipient?",
    },
    {
      id: "ex-long-term-aciclovir",
      question: "Is the patient already on a long term course of aciclovir or valaciclovir?",
    },
    {
      id: "ex-pregnancy",
      question: "Is the patient pregnant?",
    },
    {
      id: "ex-severely-immuno",
      question: "Is the patient severely immunosuppressed?",
    },
    {
      id: "ex-ckd-30",
      question: "Does the patient have CKD with eGFR < 30 ml/min/1.73m²?",
    },
    {
      id: "ex-dehydration",
      question:
        "Is the patient unable to maintain adequate hydration during treatment?",
    },
    {
      id: "ex-concurrent-nephrotoxic",
      question:
        "Is the patient taking concurrent nephrotoxic drugs (e.g. ciclosporin, tacrolimus)?",
    },
    {
      id: "ex-mycophenolate",
      question: "Is the patient currently taking mycophenolate mofetil?",
    },
    {
      id: "ex-zidovudine",
      question: "Is the patient currently taking zidovudine?",
    },
    {
      id: "ex-theophylline",
      question:
        "Is the patient currently taking theophylline (potential interaction)?",
    },
    {
      id: "ex-antiviral-recent",
      question:
        "Has the patient taken systemic antiviral therapy for shingles in the past 7 days?",
    },
  ],

  cautions: [
    {
      id: "caution-hydration",
      text: "Maintain good hydration throughout treatment to reduce risk of renal adverse effects",
      adviceText: "Encourage at least 1.5–2 L of fluid daily",
    },
    {
      id: "caution-renal",
      text: "Use with caution in patients with reduced renal function (eGFR 30–60)",
    },
    {
      id: "caution-elderly",
      text: "Elderly patients may have reduced renal clearance — monitor for neurotoxicity (confusion, drowsiness)",
    },
    {
      id: "caution-breastfeeding",
      text: "Breastfeeding — aciclovir is excreted in breast milk; consider on individual basis",
    },
  ],

  counselling: [
    "Take FIVE times a day for 7 days, approximately every 4 hours during waking hours",
    "Complete the full 7-day course even if the rash starts to improve",
    "Maintain good hydration — at least 1.5–2 L of fluid per day",
    "Report any new confusion, drowsiness, hallucinations or reduced urine output immediately",
    "Pain may persist after the rash heals (post-herpetic neuralgia) — review with GP if persistent",
    "Avoid contact with pregnant women and the immunosuppressed until lesions have crusted over",
    "Cover lesions where practical to reduce transmission risk",
    "Return for review if symptoms worsen, new neurological symptoms develop, or the rash spreads",
  ],
};
